export const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-US");

export const formatCurrency = (value, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export const formatCurrencyPrecise = (value, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

export const formatPct = (value, digits = 1) =>
  `${Number(value || 0).toFixed(digits)}%`;

export const formatFractionPct = (value, digits = 1) =>
  value == null ? "—" : formatPct(Number(value || 0) * 100, digits);

export const formatDelta = (value, digits = 1, suffix = "%") => {
  const num = Number(value || 0);
  const sign = num > 0 ? "+" : "";
  return `${sign}${num.toFixed(digits)}${suffix}`;
};

export const valueToneClass = (value, stylesRef) => {
  const num = Number(value || 0);
  if (num > 0) return stylesRef.positive;
  if (num < 0) return stylesRef.negative;
  return stylesRef.neutral;
};

const toNumeric = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const buildComparisonSubtext = (
  currentValue,
  previousValue,
  type = "number",
  currency = "USD",
) => {
  const current = toNumeric(currentValue);
  const previous = toNumeric(previousValue);
  if (current == null || previous == null) return null;

  if (type === "currency") {
    const delta = current - previous;
    const sign = delta > 0 ? "+" : delta < 0 ? "-" : "";
    return `vs ${formatCurrency(previous, currency)} (${sign}${formatCurrency(Math.abs(delta), currency)})`;
  }

  if (type === "currencyPrecise") {
    const delta = current - previous;
    const sign = delta > 0 ? "+" : delta < 0 ? "-" : "";
    return `vs ${formatCurrencyPrecise(previous, currency)} (${sign}${formatCurrencyPrecise(Math.abs(delta), currency)})`;
  }

  if (type === "pct") {
    const delta = current - previous;
    return `vs ${formatPct(previous)} (${formatDelta(delta, 1, "pp")})`;
  }

  const delta = current - previous;
  const sign = delta > 0 ? "+" : "";
  return `vs ${formatNumber(previous)} (${sign}${formatNumber(Math.abs(delta))})`;
};

export const formatWindowLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return "Unknown period";
  const start = new Date(`${startDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T00:00:00.000Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${startDate} – ${endDate}`;
  }
  const fmt = (value) =>
    value.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
};

export const buildWindowKey = (windowOrStartDate, endDate) => {
  // Accept either a window object or (startDate, endDate) pair
  if (windowOrStartDate && typeof windowOrStartDate === "object") {
    const w = windowOrStartDate;
    if (w.key) return String(w.key);
    if (w.days) return `last_${w.days}_days`;
    const start = w.startDate || w.reporting_period?.startDate || null;
    const end = w.endDate || w.reporting_period?.endDate || null;
    if (start && end) return `${start}__${end}`;
    return "unknown_window";
  }
  // Legacy two-argument call: buildWindowKey(startDate, endDate)
  return `${windowOrStartDate || ""}__${endDate || ""}`;
};

export const buildWindowEntry = (window = {}) => {
  const startDate = window?.startDate || window?.reporting_period?.startDate || null;
  const endDate = window?.endDate || window?.reporting_period?.endDate || null;
  return {
    reportId: window?.reportId || null,
    generationKey: window?.generationKey || null,
    days: window?.days || null,
    startDate,
    endDate,
    label:
      window?.label ||
      (window?.days
        ? `Last ${window.days} days`
        : formatWindowLabel(startDate, endDate)),
    key: buildWindowKey(window),
  };
};

export const resolveProductUrl = (value) => {
  if (!value) return null;
  const candidate = String(value).trim();
  if (!candidate) return null;
  try {
    if (/^https?:\/\//i.test(candidate)) return candidate;
    return new URL(candidate, window.location.origin).toString();
  } catch {
    return null;
  }
};
